import React, { useCallback, useMemo } from 'react';

import { useAppOptionsForSelect } from '@atlassian/conversation-assistant-chat-prompt-input/use-app-options-for-select';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type CreatePromptFormData,
	CreatePromptModal,
} from '@atlassian/rovo-conversation-actions/create-prompt-modal';

import { useAssistanceServiceParams } from '../../../../common/utils/use-assistance-service-params';
import { useCreatePrompt } from '../../../../controllers/prompts/useCreatePrompt';
import { usePromptsController } from '../../../../controllers/prompts/usePromptsController';

type CreatePromptModalWrapperProps = {
	initialPromptText: string;
	onClose: () => void;
};

/**
 * Wraps the CreatePromptModal with app options and prompt creation logic.
 * Only rendered when the user clicks "Create prompt", avoiding unnecessary API calls.
 */
export const CreatePromptModalWrapper = ({
	initialPromptText,
	onClose,
}: CreatePromptModalWrapperProps): React.JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'createPromptFromChatMessage',
	});
	const { options: appOptions } = useAppOptionsForSelect(assistanceServiceParams);
	const { refetch: refetchPrompts } = usePromptsController({
		assistanceServiceParams,
		enabled: false,
	});
	const { handleCreatePrompt } = useCreatePrompt({
		assistanceServiceParams,
		refetchPrompts,
	});

	const handleCreateSuccess = useCallback(() => {
		sendAnalyticsEvent({
			action: 'created',
			actionSubject: 'prompt',
			actionSubjectId: 'promptCreatedFromChatMessage',
		});
		onClose();
	}, [sendAnalyticsEvent, onClose]);

	const initialPromptData: CreatePromptFormData = useMemo(
		() => ({
			title: '',
			prompt: initialPromptText,
			useCase: null,
			role: [],
			app: [],
		}),
		[initialPromptText],
	);

	return (
		<CreatePromptModal
			appOptions={appOptions.length > 0 ? appOptions : undefined}
			initialData={initialPromptData}
			isOpen
			onClose={onClose}
			onCreate={async (data) => {
				const result = await handleCreatePrompt(data);
				return {
					...data,
					shareId: result.id,
					ownerName: result.creator?.name,
				};
			}}
			onCreateSuccess={handleCreateSuccess}
		/>
	);
};
